import { createApi } from "@reduxjs/toolkit/query/react";
import { IApiResponse, IPaginate } from "../../../interfaces/common";
import { baseQuery } from "../common";
import { ILaboratoryTestV2 } from "../../../interfaces/labV2";

export const labRequestV2Api = createApi({
  reducerPath: "labRequestV2Api",
  baseQuery: baseQuery,
  tagTypes: ["LabRequestV2Api"],

  refetchOnReconnect: true,

  endpoints: (builder) => ({

    getLabRequestV2: builder.query<IApiResponse<IPaginate<ILaboratoryTestV2[]>>, { pagination: { page: number; limit: number }, tab?: string, startDueDate?: string, endDueDate?: string, startRequestDate?: string, endRequestDate?: string, status?: string, patientId?: string }>({
      query: ({ pagination: { page = 1, limit = 10 }, tab, startDueDate, endDueDate, startRequestDate, endRequestDate, status, patientId }) =>
        `/lab-request-v2?page=${page}&limit=${limit}${tab ? `&tab=${tab}` : ''}${startDueDate ? `&startDueDate=${startDueDate}` : ''}${endDueDate ? `&endDueDate=${endDueDate}` : ''}${startRequestDate ? `&startRequestDate=${startRequestDate}` : ''}${endRequestDate ? `&endRequestDate=${endRequestDate}` : ''}${status ? `&status=${status}` : ''}${patientId ? `&patientId=${patientId}` : ''}`,
      providesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),

    createInternalLabRequestV2: builder.mutation<IApiResponse<ILaboratoryTestV2>, Partial<ILaboratoryTestV2>>({
      query: (body) => ({
        url: `/lab-request-v2/create-internal`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),

    createExternalLabRequestV2: builder.mutation<IApiResponse<ILaboratoryTestV2>, Partial<ILaboratoryTestV2>>({
      query: (body) => ({
        url: `/lab-request-v2/create-external`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    getLabRequestById: builder.query<IApiResponse<ILaboratoryTestV2>, { id: string }>({
      query: ({ id }) => `/lab-request-v2/${id}`,
      providesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    getLabRequestIdRecipients: builder.query<IApiResponse<ILaboratoryTestV2>, { id: string }>({
      query: ({ id }) => `/lab-request-v2/${id}/recipients`,
      providesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    addSample: builder.mutation<IApiResponse<ILaboratoryTestV2>, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lab-request-v2/${id}/add-sample`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    updateInternalLabRequest: builder.mutation<IApiResponse<ILaboratoryTestV2>, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lab-request-v2/${id}/edit-internal`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    updateExternalLabRequest: builder.mutation<IApiResponse<ILaboratoryTestV2>, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lab-request-v2/${id}/edit-external`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    cancelLabRequest: builder.mutation<IApiResponse<ILaboratoryTestV2>, { id: string; body:any }>({
      query: ({ id, body }) => ({
        url: `/lab-request-v2/${id}/cancel`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    addLabResult: builder.mutation<IApiResponse<ILaboratoryTestV2>, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lab-request-v2/${id}/add-result`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    addDueDate: builder.mutation<IApiResponse<ILaboratoryTestV2>, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lab-request-v2/${id}/add-due-date`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    approveLabResult: builder.mutation<IApiResponse<ILaboratoryTestV2>, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lab-request-v2/${id}/approve-result`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),
    declineLabResult: builder.mutation<IApiResponse<ILaboratoryTestV2>, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/lab-request-v2/${id}/decline-result`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),

    sendResult: builder.mutation<IApiResponse<ILaboratoryTestV2>,  { data: FormData }>({
      query: ({ data }) => ({
        url: `/lab-request-v2/send-result`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: "LabRequestV2Api", id: "LIST" }],
    }),

  }),
});

export const {
  useGetLabRequestV2Query,
  useCreateInternalLabRequestV2Mutation,
  useCreateExternalLabRequestV2Mutation,
  useGetLabRequestByIdQuery,
  useGetLabRequestIdRecipientsQuery,
  useAddSampleMutation,
  useUpdateInternalLabRequestMutation,
  useUpdateExternalLabRequestMutation,
  useCancelLabRequestMutation,
  useAddLabResultMutation,
  useAddDueDateMutation,
  useApproveLabResultMutation,
  useDeclineLabResultMutation,
  useSendResultMutation,
} = labRequestV2Api;